import * as React from 'react';

import { Mutation, MutationFn, Query } from 'react-apollo';

import ToastActions from 'actions/ToastActions';
import ErrorReporter from 'app/components/ErrorReporter';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import { InjectedRouter } from 'react-router';
import { withRouter } from 'app/context/Router';

import {
  AdminDefaultClassSettings as AdminDefaultClassSettingsQueryType,
  AdminDefaultClassSettings_organizations_classesDefaultSettings as ClassDefaultSettings,
  AdminDefaultClassSettings_organizations as Organization,
} from '../queries/__generated__/AdminDefaultClassSettings';
import {
  setOrgClassDefaults as setOrgClassDefaultsResultType,
  setOrgClassDefaultsVariables,
} from '../queries/__generated__/setOrgClassDefaults';
import { findMatchingRecord } from 'utils/arrayHelpers';

import ADMIN_DEFAULT_CLASS_SETTINGS_QUERY from '../queries/AdminDefaultClassSettingsQuery.gql';
import SET_ORG_CLASS_DEFAULTS_MUTATION from '../queries/setOrgClassDefaultsMutation.gql';

export type ChildProps = {
  classesDefaultSettings: ClassDefaultSettings;
  activeGroupCount: number;
  paidAt: string | null;
  setOrgClassDefaults: MutationFn<setOrgClassDefaultsResultType, setOrgClassDefaultsVariables>;
};

type Props = {
  groupCode: string;
  organizationUUID: string;
  children: (props: ChildProps) => React.ReactNode;
  router: InjectedRouter;
};

function AdminDefaultClassSettingsContainer(props: Props) {
  const queryVariables = { orgUuid: props.organizationUUID };

  return (
    <Mutation<setOrgClassDefaultsResultType, setOrgClassDefaultsVariables>
      mutation={SET_ORG_CLASS_DEFAULTS_MUTATION}
      onCompleted={() => ToastActions.showSuccessToast('Saved')}
      onError={error => ToastActions.showErrorToast(error.message)}
      // @ts-ignore apollo link upgrade for ts 3.7 borked it.
      refetchQueries={[{ query: ADMIN_DEFAULT_CLASS_SETTINGS_QUERY, variables: queryVariables }]}>
      {setOrgClassDefaults => (
        <Query<AdminDefaultClassSettingsQueryType>
          query={ADMIN_DEFAULT_CLASS_SETTINGS_QUERY}
          variables={queryVariables}
          fetchPolicy="network-only">
          {({ loading, error, data }) => {
            if (loading) {
              return <CenteredLoadingIndicator />;
            }

            if (!data || error) {
              return (
                <ErrorReporter
                  error={error ?? 'AdminDefaultClassSettingsContainer - could not load data'}
                />
              );
            }

            const organization = findMatchingRecord(data.organizations, props.organizationUUID);

            if (!organization) {
              return (
                <ErrorReporter
                  error={error ?? 'AdminDefaultClassSettingsContainer - could not load data'}
                />
              );
            }

            const { classesDefaultSettings, activeGroupCount, paidAt, permissions } = organization;

            if (!permissions.adminConsole.seeSettingsTabDefaultClassSettings) {
              props.router.replace(
                `/classes/${props.groupCode}/settings/organization/administrators`
              );
            }

            if (!classesDefaultSettings) {
              return (
                <ErrorReporter error="AdminDefaultClassSettingsContainer - missing default settings" />
              );
            }

            return props.children({
              classesDefaultSettings,
              activeGroupCount,
              setOrgClassDefaults,
              paidAt,
            });
          }}
        </Query>
      )}
    </Mutation>
  );
}

export { AdminDefaultClassSettingsContainer };

export default withRouter(AdminDefaultClassSettingsContainer);
